import { z } from 'zod/v4';
import { removeUndefined } from '../../util/index.js';
import { InterestPeriodDefaultBehavior } from '../case-vouchers/index.js';
import { VoucherTypeIds } from '../vouchers/index.js';
export var PaymentPlanStatus;
(function (PaymentPlanStatus) {
    PaymentPlanStatus["CREATED"] = "CREATED";
    PaymentPlanStatus["ACTIVE"] = "ACTIVE";
    PaymentPlanStatus["FINISHED"] = "FINISHED";
    PaymentPlanStatus["CANCELED"] = "CANCELED";
    PaymentPlanStatus["DELETED"] = "DELETED";
})(PaymentPlanStatus || (PaymentPlanStatus = {}));
export var PaymentPlanLogType;
(function (PaymentPlanLogType) {
    PaymentPlanLogType["ErrorSettled"] = "ERROR_SETTLED";
    PaymentPlanLogType["Settled"] = "SETTLED";
    PaymentPlanLogType["DeltaShift"] = "DELTA_SHIFT";
    PaymentPlanLogType["CancelPlan"] = "CANCEL_PLAN";
    PaymentPlanLogType["Activated"] = "ACTIVATED";
    PaymentPlanLogType["Recalculated"] = "RECALCULATED";
    PaymentPlanLogType["NoRemainingDebt"] = "NO_REMAINING_DEBT";
    PaymentPlanLogType["SettlementConfigured"] = "SETTLEMENT_CONFIGURED";
    PaymentPlanLogType["SettlementCompleted"] = "SETTLEMENT_COMPLETED";
    PaymentPlanLogType["SettlementAccordNotConfigured"] = "SETTLEMENT_ACCORD_NOT_CONFIGURED";
    PaymentPlanLogType["SettlementLapsed"] = "SETTLEMENT_LAPSED";
    // The plan's payment terms were replaced (edit, skip-to, a missed
    // installment rescheduled): a new agreement for the ÅOP
    PaymentPlanLogType["TermsChanged"] = "TERMS_CHANGED";
})(PaymentPlanLogType || (PaymentPlanLogType = {}));
export var PaymentPlanEntryStatus;
(function (PaymentPlanEntryStatus) {
    // Initial state. Entry is unpaid and the reminder date has not yet passed.
    PaymentPlanEntryStatus["PENDING"] = "PENDING";
    // Reminder date has passed and a reminder has been sent to the debtor. Still
    // unpaid; deadline has not yet passed.
    PaymentPlanEntryStatus["REMINDED"] = "REMINDED";
    // Payment has been received and verified. Terminal. Survives recalculations
    // and edits — all other entries (except AWAITING) are deleted and regenerated.
    PaymentPlanEntryStatus["SETTLED"] = "SETTLED";
    // Auto-settlement has been submitted to the payment gateway and we are
    // waiting on the gateway's response. Transient; flips to SETTLED or
    // ERRORSETTLED once the gateway confirms. Also preserved across
    // recalculations and edits so the in-flight transaction is not disturbed.
    PaymentPlanEntryStatus["AWAITING"] = "AWAITING";
    // Auto-settlement was rejected by the payment gateway. Terminal until a
    // collector intervenes manually.
    PaymentPlanEntryStatus["ERRORSETTLED"] = "ERRORSETTLED";
    // Cancel date passed without payment. Terminal — the whole plan transitions
    // to PaymentPlanStatus.CANCELED in lockstep.
    PaymentPlanEntryStatus["CANCELED"] = "CANCELED";
    // Deadline has passed without payment, but the cancel date has not. The
    // collector has been notified; entry can still settle or move to CANCELED.
    PaymentPlanEntryStatus["OVERDUE"] = "OVERDUE";
})(PaymentPlanEntryStatus || (PaymentPlanEntryStatus = {}));
export const isTerminalEntryStatus = (status) => status === PaymentPlanEntryStatus.SETTLED ||
    status === PaymentPlanEntryStatus.CANCELED;
export var PaymentPlanEntryTypes;
(function (PaymentPlanEntryTypes) {
    PaymentPlanEntryTypes["UPFRONT"] = "UPFRONT";
    PaymentPlanEntryTypes["NORMAL"] = "NORMAL";
    PaymentPlanEntryTypes["REST"] = "REST";
})(PaymentPlanEntryTypes || (PaymentPlanEntryTypes = {}));
// Statuses preserved across recalculations and edits: paid (SETTLED) and
// in-flight at the gateway (AWAITING). The backend keeps these entries
// untouched while regenerating the rest of the plan.
export const PRESERVED_ENTRY_STATUSES = [
    PaymentPlanEntryStatus.SETTLED,
    PaymentPlanEntryStatus.AWAITING,
];
export const isPreservedEntryStatus = (status) => PRESERVED_ENTRY_STATUSES.includes(status);
// Settled amounts occasionally reach the database negated (see fixErrors in
// the recalculation feature), so sum their absolute values.
export const settledAmount = (entries) => entries
    .filter((e) => e.status === PaymentPlanEntryStatus.SETTLED)
    .reduce((acc, e) => acc + Math.abs(e.amount), 0);
/* Config for a plan that replaces an existing one (edit / skip-to). Carries
   the old config by default — so a new config field survives replacement
   without every rebuild site listing it — and resets what only applies to
   original creation: upfront and initialFee were already charged, and seed
   entries only seed the first generation. settlementTotal stays the full
   agreed sum, except on a canceled plan, where the offer has lapsed and the
   replacement never inherits it. When the method changes,
   stale fields of the old method can ride along in the object; the config
   schema strips them when the server parses. Null when the old plan has no
   config to carry. */
export const replacementConfig = (paymentPlan, overrides) => {
    const config = paymentPlan.config;
    if (config == null) {
        return null;
    }
    return {
        ...config,
        amountUpfront: null,
        dateUpfront: null,
        plan: undefined,
        feeStructure: config.feeStructure?.perEntryFee
            ? { perEntryFee: config.feeStructure.perEntryFee }
            : undefined,
        settlementTotal: paymentPlan.status === PaymentPlanStatus.CANCELED
            ? null
            : config.settlementTotal,
        ...overrides,
    };
};
/* Duration for a replacement plan: the regular entries still to be paid. An
   unpaid upfront entry doesn't count, as replacementConfig drops the upfront. */
export const replacementDuration = (paymentPlan) => Math.max(1, paymentPlan.plan.filter((entry) => !isPreservedEntryStatus(entry.status) &&
    entry.type !== PaymentPlanEntryTypes.UPFRONT).length);
/* The preserved entries a replacement plan is generated against, for the
   server to verify. Undefined for a canceled plan: the server replaces it with
   a fresh plan, so nothing is preserved. */
export const replacementPreservedEntryIds = (paymentPlan) => paymentPlan.status === PaymentPlanStatus.CANCELED
    ? undefined
    : paymentPlan.plan.flatMap((entry) => isPreservedEntryStatus(entry.status) && entry.paymentPlanEntryId != null
        ? [entry.paymentPlanEntryId]
        : []);
// Base shape of a single plan entry — the wire / API representation that every
// entry must satisfy. `PlanEntry` and `NewPlanEntry` are derived from this so
// the schema and the TS types can never drift apart.
export const PaymentPlanEntrySchema = z.object({
    amount: z.number(),
    cancel: z.string(),
    date: z.string(),
    reminder: z.string(),
    deadline: z.string(),
    type: z.enum(PaymentPlanEntryTypes),
});
// A stored / loaded entry, augmented with status and a few client-side
// niceties.
export const PlanEntrySchema = PaymentPlanEntrySchema.extend({
    status: z.enum(PaymentPlanEntryStatus),
    interestAmount: z.number().nullable().optional(),
    key: z.string().optional(),
    paymentPlanEntryId: z.string().optional(),
});
// A new entry being submitted to the API; status is optional (defaulted by
// the server) and the other fields match the base schema.
export const NewPlanEntrySchema = PaymentPlanEntrySchema.extend({
    status: z.enum(PaymentPlanEntryStatus).optional(),
});
export var Interval;
(function (Interval) {
    Interval["LASTBANKDAYMONTH"] = "LASTBANKDAYMONTH";
    Interval["WEEK"] = "WEEK";
    Interval["TWO-WEEKS"] = "TWO-WEEKS";
    Interval["MONTH"] = "MONTH";
    Interval["TWO-MONTHS"] = "TWO-MONTHS";
    Interval["QUARTERLY"] = "QUARTERLY";
    Interval["YEARLY"] = "YEARLY";
})(Interval || (Interval = {}));
// Stored in the details of the vouchers a plan books, keyed like
// PaymentPlanFeeStructure
export var PaymentPlanFeeType;
(function (PaymentPlanFeeType) {
    PaymentPlanFeeType["InitialFee"] = "initialFee";
    PaymentPlanFeeType["PerEntryFee"] = "perEntryFee";
})(PaymentPlanFeeType || (PaymentPlanFeeType = {}));
// Default voucher type ID for payment plan fees (Admin costs)
export const DEFAULT_FEE_VOUCHER_TYPE_ID = VoucherTypeIds.ADMINISTRATIONCOST;
export const PaymentPlanFeeStructureSchema = z.object({
    initialFee: z
        .object({
        amount: z.number(),
        voucherTypeId: z.uuid(),
    })
        .optional(),
    perEntryFee: z
        .object({
        amount: z.number(),
        voucherTypeId: z.uuid(),
    })
        .optional(),
});
/* A plan runs at most this many years of entries. A plan whose payment trails
   the interest stops there, its REST taking all interest projected until
   then, so a longer plan overflows payment_plan_entries.amount (bigint). */
export const MAX_PLAN_YEARS = 30;
const ENTRIES_PER_YEAR = {
    [Interval.WEEK]: 52,
    [Interval['TWO-WEEKS']]: 26,
    [Interval.MONTH]: 12,
    [Interval.LASTBANKDAYMONTH]: 12,
    [Interval['TWO-MONTHS']]: 6,
    [Interval.QUARTERLY]: 4,
    [Interval.YEARLY]: 1,
};
export const maxPlanEntries = (interval) => MAX_PLAN_YEARS * ENTRIES_PER_YEAR[interval];
const durationWithinMaxPlanEntries = (plan, ctx) => {
    const maxEntries = maxPlanEntries(plan.interval);
    if (plan.duration != null && plan.duration > maxEntries) {
        ctx.addIssue({
            code: 'custom',
            path: ['duration'],
            message: `A ${plan.interval} plan can have at most ${maxEntries} entries`,
        });
    }
};
/* Smallest REST entry worth charging: plain plans drop a tail below this,
   settlement plans fold it into the final entry. TODO: Currencies */
export const MIN_REST_AMOUNT = 2500;
const PaymentPlanConfigSchema = z
    .object({
    startDate: z.string(),
    interval: z.enum(Interval),
    amountUpfront: z.number().nullish(),
    dateUpfront: z.string().nullish(),
    // Accord (settlement): the full agreed sum; entries preserved across
    // edits count toward it.
    // When set, generation targets this instead of amortizing the balance
    // to zero, and the debt left when the plan finishes is forgiven.
    settlementTotal: z.number().positive().nullish(),
    interestFree: z
        .object({
        from: z.string(),
        to: z.string().nullable(),
        // On default: KeepFrozen ends the freeze at the default date,
        // Reapply voids it retroactively.
        onDefault: z.enum(InterestPeriodDefaultBehavior).nullish(),
    })
        .nullish(),
    feeStructure: PaymentPlanFeeStructureSchema.transform((x) => x ?? undefined).nullish(),
    // Unset follows the creditor's, then the tenant's,
    // autoRecalculateOnSettlement.
    autoRecalculateOnSettlement: z.boolean().nullish(),
    // Seed entries for the generator. `status` is only meaningful for
    // migrations seeding historical entries (e.g. SETTLED); the server
    // defaults it to PENDING and no server path turns client-supplied
    // config seeds into entries directly.
    plan: z
        .array(NewPlanEntrySchema)
        .transform((x) => x ?? undefined)
        .nullish(),
})
    .and(z.discriminatedUnion('method', [
    z.object({
        method: z.literal('duration'),
        duration: z.number().int().positive(),
    }),
    z.object({
        method: z.literal('amount'),
        amount: z.number().positive(),
        scheduledAmountChanges: z
            .array(z.object({
            amount: z.number().positive(),
            date: z.string(),
        }))
            .transform((x) => x ?? undefined)
            .nullish(),
    }),
]))
    .superRefine(durationWithinMaxPlanEntries);
export var PaymentPlanConsoleLogType;
(function (PaymentPlanConsoleLogType) {
    PaymentPlanConsoleLogType["REMINDER"] = "reminder";
    PaymentPlanConsoleLogType["DEADLINE"] = "deadline";
    PaymentPlanConsoleLogType["CANCEL"] = "cancel";
    PaymentPlanConsoleLogType["HANDLER"] = "handler";
    PaymentPlanConsoleLogType["SETTLECARDS"] = "settle-cards";
    PaymentPlanConsoleLogType["SETTLEBSANDLS"] = "settle-bs-and-ls";
    PaymentPlanConsoleLogType["SettleRanTwice"] = "settle-ran-twice";
})(PaymentPlanConsoleLogType || (PaymentPlanConsoleLogType = {}));
export const PaymentPlansConfigSchema = z.object({
    plans: z.array(z
        .object({
        interval: z.enum(Interval),
        duration: z.number().int().positive(),
        id: z.uuid(),
    })
        .superRefine(durationWithinMaxPlanEntries)),
    disablePaymentPlans: z.boolean().optional(),
    requireCentralRegisterId: z.boolean().optional(),
    minEntryAmount: z.number(),
    activationDeadlineDays: z.number().optional().nullable(),
    deadlineDays: z.number().optional(),
    cancelDays: z.number().optional(),
    reminderDays: z.number().optional(),
    defaultFeeStructure: PaymentPlanFeeStructureSchema.optional(),
    // When true, every settled installment enqueues a recalculation of the
    // remaining schedule. Lets creditors keep amounts/duration accurate as
    // interest accrues or extra payments come in. Off by default to preserve
    // existing behaviour.
    autoRecalculateOnSettlement: z.boolean().optional(),
    // Fallback for interest-free onDefault when the plan does not specify one
    interestFreeOnDefault: z.enum(InterestPeriodDefaultBehavior).optional(),
});
export const defaultPaymentPlansConfig = {
    plans: [
        {
            id: '300be1dd-63cb-43f0-a110-eabaa0416297',
            interval: Interval.MONTH,
            duration: 3,
        },
        {
            id: 'e38c6d93-09d9-40d5-9611-1324c9ea9c83',
            interval: Interval.MONTH,
            duration: 6,
        },
        {
            id: '3cd046f4-650d-43bd-9091-43d12e9eb8ea',
            interval: Interval.MONTH,
            duration: 9,
        },
        {
            id: 'c575de9e-25a3-4b64-ad7b-402ffb40d9d1',
            interval: Interval.MONTH,
            duration: 12,
        },
    ],
    minEntryAmount: 5000,
    disablePaymentPlans: false,
    requireCentralRegisterId: false,
    deadlineDays: 5,
    cancelDays: 5,
    reminderDays: 5,
    activationDeadlineDays: 14,
};
// The creditor config overrides the tenant's per field, and the tenant's the
// defaults. An unset field inherits, e.g. a creditor without its own
// defaultFeeStructure charges the tenant's fees.
export const resolvePaymentPlansConfig = (creditorConfig, tenantConfig) => ({
    ...defaultPaymentPlansConfig,
    ...removeUndefined(tenantConfig ?? {}),
    ...removeUndefined(creditorConfig ?? {}),
});
export const getCurrentPaymentPlan = (paymentPlans) => {
    if (!paymentPlans) {
        return null;
    }
    const currentActivePlan = paymentPlans.find((plan) => [PaymentPlanStatus.ACTIVE].includes(plan.status));
    if (currentActivePlan) {
        return currentActivePlan;
    }
    const currentCreatedPlan = paymentPlans.find((plan) => [PaymentPlanStatus.CREATED].includes(plan.status));
    if (currentCreatedPlan) {
        return currentCreatedPlan;
    }
    const currentPlan = paymentPlans.find((plan) => [PaymentPlanStatus.FINISHED].includes(plan.status));
    return currentPlan || null;
};
export const getAmount = {
    upfront: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.UPFRONT);
        return entry && entry.amount;
    },
    entry: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.NORMAL);
        return entry && entry.amount;
    },
    total: (plan) => plan.reduce((acc, entry) => acc + (entry.amount || 0), 0),
    rest: (plan) => {
        const entry = plan.find((p) => p.type === PaymentPlanEntryTypes.REST);
        return entry && entry.amount;
    },
};
export const CreatePaymentPlanSchema = z
    .object({
    activationTrigger: z.boolean().optional(),
    selectedPaymentPlanId: z.uuid().optional(),
    plan: z
        .array(PaymentPlanEntrySchema)
        .nullish()
        .transform((x) => x ?? undefined),
    config: PaymentPlanConfigSchema.nullish().transform((x) => x ?? null),
    preservedEntryIds: z.array(z.uuid()).optional(),
})
    .refine((body) => body.selectedPaymentPlanId == null || body.config == null, {
    message: 'A selected payment plan takes its config from the preset',
});
export const PatchPaymentPlanSchema = z.object({
    status: z.enum(PaymentPlanStatus).optional(),
});
export const calculateInterestPerEntry = (currentInterest, entries, index) => {
    const entry = entries[index];
    const previousEntry = entries[index - 1];
    if (!entry) {
        throw 'Could not find entry with that index';
    }
    if (!previousEntry) {
        return currentInterest - (entry.interestAmount || 0);
    }
    else {
        return (previousEntry.interestAmount || 0) - (entry.interestAmount || 0);
    }
};
//# sourceMappingURL=index.js.map